import type { MemoryPoolDistribution } from '@root/models';

/** Colors per segment group + the "required" (assumed space) pseudo-segment. */
export const MEMORY_SEGMENT_COLORS: Record<string, string> = {
  ollama: '#4a9eff',
  'ollama-weights': '#4a9eff',
  'ollama-kv': '#2dd4bf',
  'ollama-ssm': '#a78bfa',
  'ollama-draft': '#38bdf8',
  'ollama-vision': '#f472b6',
  'ollama-compute': '#93c5fd',
  comfyui: '#f59e0b',
  unknown: '#6b7280',
  required: '#22c55e',
  requiredOver: '#ef4444',
};

export interface MemoryDonutSegment {
  key: string;
  label: string;
  group: string;
  sizeGb: number;
  contextSize?: number | null;
  /** Color override (used by the "required" segment). */
  color?: string;
  /** Dashed stroke style (used for unknown). */
  dashed?: boolean;
  model?: string;
  /** Size comes from the measured Ollama-log breakdown, not /api/ps. */
  measured?: boolean;
}

interface MemoryDonutProps {
  pool: MemoryPoolDistribution;
  /** Segments to draw; defaults to pool.segments. */
  segments?: MemoryDonutSegment[];
  hoverKey?: string | null;
  onHover?: (key: string | null) => void;
  size?: number;
  strokeWidth?: number;
  /** Center label override. */
  centerTop?: string;
  centerBottom?: string;
}

/** Polar-to-cartesian helper for arc paths (angles in degrees, 0 = top). */
function polarPoint(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/** SVG path for a ring segment between startAngle and endAngle (degrees). */
function ringArcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const [x1, y1] = polarPoint(cx, cy, r, startAngle);
  const [x2, y2] = polarPoint(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${x1.toFixed(3)} ${y1.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`;
}

/**
 * SVG donut chart of a memory pool (VRAM or RAM). Segments are drawn as stroked
 * arcs; hovering a segment (via onHover) thickens it and dims the others. The
 * "unknown" segment uses a dashed stroke. Center shows used/total.
 */
export function MemoryDonut({ pool, segments, hoverKey, onHover, size = 132, strokeWidth = 22, centerTop, centerBottom }: MemoryDonutProps) {
  const segs: MemoryDonutSegment[] = (segments ?? pool.segments).map(s => {
    const src = s as MemoryDonutSegment;
    return {
      ...src,
      color: src.color ?? MEMORY_SEGMENT_COLORS[src.group],
      dashed: src.dashed ?? src.group === 'unknown',
    };
  });

  const total = pool.totalGb;
  const drawnTotal = segs.reduce((sum, s) => sum + s.sizeGb, 0);
  // Scale so drawn segments fill the ring proportionally to the pool total;
  // if segments exceed total (overflow), scale to their own sum.
  const scaleBase = Math.max(total, drawnTotal, 0.001);

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth * 1.6) / 2;

  let angle = 0;
  const arcs = segs.filter(s => s.sizeGb > 0).map(seg => {
    const sweep = (seg.sizeGb / scaleBase) * 360;
    const start = angle;
    angle += sweep;
    return { seg, start, sweep };
  });

  const usedPct = total > 0 ? Math.min(100, (pool.usedGb / total) * 100) : 0;

  return (
    <div className="memory-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {/* track */}
        <circle className="memory-donut-track" cx={cx} cy={cy} r={r} fill="none" strokeWidth={strokeWidth} />
        {/* segments */}
        {arcs.map(({ seg, start, sweep }) => {
          const isHovered = hoverKey === seg.key;
          const dimmed = hoverKey != null && hoverKey !== seg.key;
          // Full circles need two arcs (SVG can't draw 360° in one path)
          const path = sweep >= 359.99
            ? ringArcPath(cx, cy, r, 0, 180) + ` M ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy}`
            : ringArcPath(cx, cy, r, start, start + Math.max(0.2, sweep - 0.2));
          return (
            <path
              key={seg.key}
              className={`memory-donut-segment${isHovered ? ' hovered' : ''}${dimmed ? ' dimmed' : ''}`}
              d={path}
              fill="none"
              stroke={seg.color}
              strokeWidth={isHovered ? strokeWidth * 1.28 : strokeWidth}
              strokeDasharray={seg.dashed ? '6 5' : undefined}
              strokeLinecap="butt"
              onMouseEnter={onHover ? () => onHover(seg.key) : undefined}
              onMouseLeave={onHover ? () => onHover(null) : undefined}
            />
          );
        })}
      </svg>
      <div className="memory-donut-center">
        <div className="memory-donut-center-top">{centerTop ?? `${pool.usedGb.toFixed(1)}`}</div>
        <div className="memory-donut-center-bottom">
          {centerBottom ?? `/ ${total.toFixed(0)} GB`}
          <span className="memory-donut-center-pct">{usedPct.toFixed(0)}%</span>
        </div>
      </div>
    </div>
  );
}
